import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';
import Stars from './Stars';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const REASONS = {
  login: <>Purchased this piece? <Link to="/login" className="text-[#C6410E] link-underline">Sign in</Link> to leave a review.</>,
  not_purchased: 'Reviews are open to verified buyers of this piece. Once your order is placed, you can share your thoughts here.',
  already_reviewed: 'Thank you — your review of this piece is already published.',
};

function ReviewForm({ productId, onDone }) {
  const [form, setForm] = useState({ rating: 0, title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast({ title: 'Choose a star rating first' });
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${productId}/reviews`, form);
      toast({ title: 'Review published', description: 'Thank you for sharing.' });
      onDone(data);
    } catch (err) {
      toast({ title: 'Could not publish', description: err.response?.data?.detail || 'Please try again' });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="border border-black/10 p-6 md:p-8 space-y-6 bg-[#E9E1D4]/30">
      <div>
        <p className="text-[11px] tracking-widest-plus uppercase mb-3">Your rating</p>
        <Stars value={form.rating} size="w-6 h-6" onChange={r => setForm({ ...form, rating: r })} testId="review-rating" />
      </div>
      <div>
        <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Title</label>
        <input data-testid="review-title-input" required minLength={2} maxLength={80} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="In three or four words" className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm" />
      </div>
      <div>
        <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Your review</label>
        <textarea data-testid="review-body-input" required minLength={10} maxLength={1000} rows={4} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="How does it feel in hand? How has it aged?" className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm resize-none" />
      </div>
      <button data-testid="review-submit-btn" disabled={busy} className="btn-primary">{busy ? 'Publishing…' : 'Publish review'}</button>
    </form>
  );
}

export default function Reviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0, distribution: {} });
  const [elig, setElig] = useState({ can_review: false, reason: 'login' });

  useEffect(() => {
    api.get(`/products/${productId}/reviews`).then(r => setData(r.data)).catch(() => {});
  }, [productId]);

  useEffect(() => {
    api.get(`/products/${productId}/reviews/eligibility`).then(r => setElig(r.data)).catch(() => {});
  }, [productId, user]);

  const onPublished = ({ review, average, count }) => {
    setData(d => ({ ...d, reviews: [review, ...d.reviews], average, count }));
    setElig({ can_review: false, reason: 'already_reviewed' });
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="max-w-7xl mx-auto px-6 py-20 border-t border-black/10">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">From our customers</p>
          <h2 className="font-serif-display text-4xl mb-6">Reviews</h2>
          {data.count > 0 ? (
            <>
              <div className="flex items-end gap-3 mb-2">
                <span data-testid="reviews-average" className="font-serif-display text-6xl leading-none">{data.average.toFixed(1)}</span>
                <div className="pb-1">
                  <Stars value={data.average} size="w-4 h-4" />
                  <p data-testid="reviews-count" className="text-xs text-[#1a1a1a]/60 mt-1">{data.count} verified review{data.count > 1 ? 's' : ''}</p>
                </div>
              </div>
              <ul className="mt-6 space-y-1.5">
                {[5, 4, 3, 2, 1].map(i => {
                  const n = data.distribution?.[i] || 0;
                  return (
                    <li key={i} className="flex items-center gap-3 text-xs">
                      <span className="w-3 text-right">{i}</span>
                      <div className="flex-1 h-1 bg-black/10"><div className="h-full bg-[#C6410E]" style={{ width: `${data.count ? (n / data.count) * 100 : 0}%` }} /></div>
                      <span className="w-4 text-[#1a1a1a]/50">{n}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p data-testid="reviews-empty" className="text-[#1a1a1a]/60 text-sm leading-relaxed">No reviews yet. The first word on this piece is still to be written.</p>
          )}
        </div>

        <div className="md:col-span-2 space-y-10">
          {elig.can_review ? <ReviewForm productId={productId} onDone={onPublished} /> : (
            <p data-testid="review-eligibility-note" className="text-sm text-[#1a1a1a]/60 border-l-2 border-[#C6410E]/40 pl-4">{REASONS[elig.reason] || REASONS.not_purchased}</p>
          )}

          <ul data-testid="reviews-list" className="divide-y divide-black/10">
            {data.reviews.map(r => (
              <li key={r.id} data-testid={`review-${r.id}`} className="py-7">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <Stars value={r.rating} />
                  <span className="text-[11px] text-[#1a1a1a]/50">{new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <h3 className="font-serif-display text-xl mb-2">{r.title}</h3>
                <p className="text-sm text-[#1a1a1a]/75 leading-relaxed mb-3">{r.body}</p>
                <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60 flex items-center gap-1.5">
                  {r.author} {r.verified && <><span className="mx-1">·</span><BadgeCheck className="w-3.5 h-3.5 text-[#C6410E]" /> Verified buyer</>}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
